import React from 'react';
import PropTypes from 'prop-types';
import { Label } from 'components/Typography';
import get from 'lodash/get';
import Input from '../Input';
import { Container, Optional, Error, CheckIcon, StyledTooltip, Comment } from './styled';

const TextInput = ({
  label,
  comment,
  placeholder,
  tooltip,
  tooltipVariant,
  variant,
  disabled,
  size,
  spacing,
  children,
  formik,
  name,
  type,
  optional,
  onKeyDown,
  inputMode,
  validateWhileTyping,
  className
}) => {
  const getInputProps = () => {
    const rest = formik && formik.getFieldProps(name);
    return {
      placeholder,
      type,
      variant,
      spacing,
      inputMode,
      disabled,
      onKeyDown,
      ...rest
    };
  };

  const validateWhileTypingOrBlur = () =>
    // show validation errors after 5 characters have been typed
    // or user has clicked away from field
    (get(formik.values, name) && get(formik.values, name).length > 5) ||
    get(formik.touched, name);

  const getTooltipVariant = () => {
    if (tooltipVariant) return tooltipVariant;
    return variant === 'block' ? 'light' : variant;
  };

  const renderLabel = (
    <Label htmlFor={name} variant={variant} size={size} spacing={spacing}>
      {label}
      {tooltip && <StyledTooltip variant={getTooltipVariant()}>{tooltip}</StyledTooltip>}
      {optional && <Optional>- optional</Optional>}
      {formik && !optional && (
        <>
          {(validateWhileTyping
            ? validateWhileTypingOrBlur()
            : get(formik.touched, name)) && get(formik.errors, name) ? (
            <Error>{get(formik.errors, name)}</Error>
          ) : null}
          {!get(formik.errors, name) && formik.dirty && <CheckIcon />}
        </>
      )}
      {comment && <Comment>{comment}</Comment>}
    </Label>
  );

  return (
    <Container size={size} className={className}>
      {label && renderLabel}
      <Input {...getInputProps()} />
      {children}
    </Container>
  );
};

TextInput.propTypes = {
  formik: PropTypes.object,
  name: PropTypes.string.isRequired,
  optional: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  comment: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  tooltip: PropTypes.string,
  tooltipVariant: PropTypes.string,
  placeholder: PropTypes.string,
  variant: PropTypes.string,
  children: PropTypes.any,
  type: PropTypes.string,
  spacing: PropTypes.string,
  className: PropTypes.string,
  onKeyDown: PropTypes.func,
  disabled: PropTypes.bool,
  inputMode: PropTypes.string,
  validateWhileTyping: PropTypes.bool,
  size: PropTypes.oneOf(['medium', 'large', 'extralarge'])
};

TextInput.defaultProps = {
  label: '',
  comment: '',
  tooltip: '',
  placeholder: '',
  optional: false,
  variant: 'dark',
  size: 'medium',
  children: [],
  type: 'text',
  spacing: 'normal'
};

export default TextInput;
